import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from "react";
import { useReveal, useTouch, type RevealController } from "@/hooks/useReveal";
import {
  HINT_SIZE_MOUSE,
  HINT_SIZE_TOUCH,
  MAX_HINTS,
  bandMask,
  claimGridPulse,
  fitRect,
  gridPlan,
  halfFor,
  hasRevealedBefore,
  hintSpot,
  isPhone,
  noSave,
  revealRegions,
  sizeOf,
  spotUrl,
  tallOk,
  warm,
  LAYERS,
  type Rect,
  type RevealLayer,
  type RevealProtect,
  type RevealRegion,
} from "@/lib/revealKit";

// The UI half of a port of photohawk-app's RevealKit (+ SpotlightImage), so photographers see protected
// photos exactly as buyers do: the person icons, the reveal images, the slider / play-pause, the hint
// pill and the styles. Same sizes, colours, positions and Material icons as the app.

// ---- Material icons (the app's @material-ui/icons 4.11 glyphs: Close, Pause, PlayArrow, TouchApp)
const MUI_PATHS = {
  close: "M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z",
  pause: "M6 19h4V5H6v14zm8-14v14h4V5h-4z",
  play: "M8 5v14l11-7z",
  tap: "M9 11.24V7.5C9 6.12 10.12 5 11.5 5S14 6.12 14 7.5v3.74c1.21-.81 2-2.18 2-3.74C16 5.01 13.99 3 11.5 3S7 5.01 7 7.5c0 1.56.79 2.93 2 3.74zm9.84 4.63l-4.54-2.26c-.17-.07-.35-.11-.54-.11H13v-6c0-.83-.67-1.5-1.5-1.5S10 6.67 10 7.5v10.74c-3.6-.76-3.54-.75-3.67-.75-.31 0-.59.13-.79.33l-.79.8 4.94 4.94c.27.27.65.44 1.06.44h6.79c.75 0 1.33-.55 1.44-1.28l.75-5.27c.01-.07.02-.14.02-.2 0-.62-.38-1.16-.91-1.38z",
};
type IconName = keyof typeof MUI_PATHS;
/** An MUI SvgIcon look-alike: 1em square (fontSize 24px by default), currentColor. */
export const MuiIcon = ({ name, style }: { name: IconName; style?: CSSProperties }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    data-icon={name}
    style={{ width: "1em", height: "1em", display: "inline-block", fontSize: 24, flexShrink: 0, fill: "currentColor", userSelect: "none", ...style }}
  >
    <path d={MUI_PATHS[name]} />
  </svg>
);


const useBoxSize = (ref: RefObject<HTMLElement>) => {
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () =>
      setSize((prev) =>
        prev && prev.width === el.clientWidth && prev.height === el.clientHeight ? prev : { width: el.clientWidth, height: el.clientHeight },
      );
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [ref]);
  return size;
};

/**
 * The reveal on screen: `frame`, with the band's half image stacked exactly on top and masked to the
 * band, in a wrapper laid out at the reveal's own aspect (contain, or cover on a phone) so the mask
 * percentages are percentages of the image. Nothing is drawn until frame and both halves are decoded.
 */
export const RevealImages = ({ ctl, fit = "contain", background = null }: { ctl: RevealController; fit?: "contain" | "cover"; background?: string | null }) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const box = useBoxSize(boxRef);
  const { active, urlFor, winFor, bandHeight, halves, layersReady } = ctl;
  const frame = layersReady && active ? urlFor(active.i, "frame") : undefined;
  const size = sizeOf(frame);
  const win = active ? winFor(active.i) : null;
  const region = active ? ctl.regions[active.i] : null;
  const rect = size && box && box.width && box.height ? fitRect(box.width, box.height, size.width, size.height, fit) : null;
  const want = active ? halfFor(active.c) : null;
  const mask = rect && win && region && active ? bandMask(region, win, active.c, bandHeight, halves) : null;
  const imgStyle: CSSProperties = { position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: "block", pointerEvents: "none", ...noSave };
  return (
    <div ref={boxRef} style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", background: rect && background ? background : "transparent" }}>
      {rect && active && (
        <div data-phx-reveal="" style={{ position: "absolute", ...rect }}>
          <img src={frame} alt="" draggable={false} data-phx-layer="frame" style={{ ...imgStyle, zIndex: 1 }} />
          {mask &&
            (["top", "bottom"] as const).map((layer) => {
              const on = layer === want;
              return (
                <img
                  key={layer}
                  src={urlFor(active.i, layer)}
                  alt=""
                  draggable={false}
                  data-phx-layer={layer}
                  style={{ ...imgStyle, zIndex: 2, visibility: on ? "visible" : "hidden", WebkitMaskImage: on ? mask : "none", maskImage: on ? mask : "none" }}
                />
              );
            })}
        </div>
      )}
      {ctl.loading && (
        <div data-phx-loading="" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 4, pointerEvents: "none" }}>
          <div className="phx-spot-spinner" />
        </div>
      )}
    </div>
  );
};

/**
 * The icons over every revealable person (largest first, at most 12): the same tapping hand on every
 * device (touch and mouse). Click-through (targets underneath do the work); `hidden` fades them while a reveal shows.
 * They pulse a few times when first shown, unless reduced motion or someone was revealed this session.
 */
export const PersonHints = ({
  regions,
  rect,
  touch,
  hidden,
  zIndex = 4,
  activeIndex = null,
}: {
  regions: RevealRegion[];
  rect: Rect | null;
  touch: boolean;
  hidden: boolean;
  zIndex?: number;
  /** The person shown in the split pane beside the lightbox: ringed. */
  activeIndex?: number | null;
}) => {
  const [pulse] = useState(() => !hasRevealedBefore());
  if (!rect || !regions.length) return null;
  const base = touch ? HINT_SIZE_TOUCH : HINT_SIZE_MOUSE;
  return (
    <div
      aria-hidden="true"
      data-phx-hints={hidden ? "hidden" : "shown"}
      style={{ position: "absolute", inset: 0, zIndex, pointerEvents: "none", opacity: hidden ? 0 : 1, transition: `opacity ${hidden ? 0.15 : 0.3}s ease`, ...noSave }}
    >
      {regions.slice(0, MAX_HINTS).map((r, i) => {
        const spot = hintSpot(r, rect, base);
        if (!spot) return null;
        return (
          <div
            key={i}
            data-phx-hint={i}
            data-phx-hint-active={i === activeIndex ? "" : undefined}
            className={pulse ? "phx-person-hint phx-person-hint-pulse" : "phx-person-hint"}
            style={{
              position: "absolute",
              left: spot.x - spot.size / 2,
              top: spot.y - spot.size / 2,
              width: spot.size,
              height: spot.size,
              borderRadius: "50%",
              background: i === activeIndex ? "rgba(0, 0, 0, 0.7)" : "rgba(0, 0, 0, 0.5)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: i === activeIndex ? "0 0 0 2px #fff, 0 2px 8px rgba(0, 0, 0, 0.45)" : "0 2px 8px rgba(0, 0, 0, 0.45)",
              transition: "box-shadow 0.2s ease, background 0.2s ease",
              pointerEvents: "none",
            }}
          >
            <MuiIcon name="tap" style={{ fontSize: Math.round(spot.size * 0.6), filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.4))" }} />
          </div>
        );
      })}
    </div>
  );
};

const iconDisc: CSSProperties = {
  borderRadius: "50%",
  background: "rgba(0, 0, 0, 0.5)",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.45)",
};

/**
 * The overlay for one grid thumbnail (fills the tile; the photo is object-cover cropped into it):
 * the tap icon on the body of each of the largest people (at most 3, largest first), kept inside
 * the visible tile; a click on a person (icon or box) opens the viewer on them (region index i).
 * No layers are fetched from the grid except a person's own, on pointer-down / hovering their icon.
 */
export const GridPersonHints = ({
  protect,
  base,
  aspect,
  onOpen,
  splitView = false,
}: {
  protect: RevealProtect | null | undefined;
  base?: string;
  aspect: number;
  onOpen: (i: number) => void;
  /** Reveals open in the desktop split pane (portrait reveals): warm those. */
  splitView?: boolean;
}) => {
  const regions = revealRegions(protect);
  const tileRef = useRef<HTMLDivElement>(null);
  const tile = useBoxSize(tileRef);
  const touch = useTouch();
  const rect = tile && tile.width && tile.height && aspect > 0 ? fitRect(tile.width, tile.height, aspect, 1, "cover") : null;
  const plan = regions.length ? gridPlan(regions, rect, tile?.width ?? 0, tile?.height ?? 0, touch) : null;
  const [pulse] = useState(() => regions.length > 0 && claimGridPulse());
  if (!regions.length) return null;
  const shape = (isPhone() || splitView) && tallOk(regions) ? "tall" : null;
  const warmPerson = (i: number) => { if (base) LAYERS.forEach((layer) => warm(spotUrl(base, i, layer, shape))); };
  return (
    <div ref={tileRef} style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      {plan && (
        <>
          {pulse && <RevealStyles />}
          {plan.people.map(({ spot, box }, i) => (
            <button
              type="button"
              key={i}
              aria-label={`Show person ${i + 1} clearly, full screen`}
              data-phx-grid-person={i}
              onPointerDown={() => warmPerson(i)}
              onClick={(e) => { e.stopPropagation(); onOpen(i); }}
              onContextMenu={(e) => e.preventDefault()}
              style={{ position: "absolute", zIndex: 1, ...box, padding: 0, border: "none", background: "transparent", cursor: "zoom-in", touchAction: "manipulation", pointerEvents: "auto", ...noSave }}
            >
              <span
                aria-hidden="true"
                data-phx-hint={i}
                className={pulse ? "phx-person-hint phx-person-hint-pulse" : "phx-person-hint"}
                onMouseEnter={() => warmPerson(i)}
                style={{ position: "absolute", left: spot.x - box.left - spot.size / 2, top: spot.y - box.top - spot.size / 2, width: spot.size, height: spot.size, ...iconDisc }}
              >
                <MuiIcon name="tap" style={{ fontSize: Math.round(spot.size * 0.6), filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.4))" }} />
              </span>
            </button>
          ))}
        </>
      )}
    </div>
  );
};

/**
 * Tap / click anywhere on the photo to put a reveal away (only a press that started on it). `onBack`:
 * where "back" goes instead of the whole photo in the same view (a reveal opened on a person over the
 * lightbox goes back to the lightbox).
 */
export const GoBackLayer = ({ ctl, zIndex = 7, onBack = null }: { ctl: RevealController; zIndex?: number; onBack?: (() => void) | null }) => {
  const pressed = useRef(false);
  if (ctl.active === null) return null;
  const back = () => (onBack ? onBack() : ctl.hide());
  return (
    <button
      type="button"
      aria-label="Go back"
      onPointerDown={(e) => { e.stopPropagation(); pressed.current = true; }}
      onTouchStart={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        if (pressed.current) back();
        pressed.current = false;
      }}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); back(); } }}
      onContextMenu={(e) => e.preventDefault()}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex, padding: 0, border: "none", background: "transparent", cursor: "zoom-out", ...noSave }}
    />
  );
};

/** Play / pause of the auto-sweep. */
export const PlayPauseButton = ({ playing, onToggle, style }: { playing: boolean; onToggle: () => void; style?: CSSProperties }) => (
  <button
    type="button"
    aria-label={playing ? "Pause scanning" : "Play scanning"}
    aria-pressed={playing}
    title={playing ? "Pause" : "Play"}
    data-phx-playpause={playing ? "playing" : "paused"}
    onPointerDown={(e) => e.stopPropagation()}
    onTouchStart={(e) => e.stopPropagation()}
    onClick={(e) => { e.stopPropagation(); onToggle(); }}
    onContextMenu={(e) => e.preventDefault()}
    style={{
      width: 44,
      height: 44,
      borderRadius: 22,
      border: "none",
      padding: 0,
      background: "rgba(0,0,0,0.55)",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      touchAction: "manipulation",
      ...noSave,
      ...style,
    }}
  >
    <MuiIcon name={playing ? "pause" : "play"} />
  </button>
);

/**
 * "See whole photo": a small card in the reveal's top-left corner (clear of the × top right, the slider
 * and play / pause on the right edge and the hint pill at the bottom) — a thumbnail of the whole photo
 * over its label — that leaves the reveal for the photo's lightbox.
 */
export const WholePhotoButton = ({ thumbSrc, label, onClick }: { thumbSrc?: string; label: string; onClick: () => void }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    className="phx-whole-photo"
    data-phx-whole-photo=""
    onPointerDown={(e) => e.stopPropagation()}
    onTouchStart={(e) => e.stopPropagation()}
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    onContextMenu={(e) => e.preventDefault()}
    style={{
      position: "absolute",
      top: "calc(env(safe-area-inset-top, 0px) + 10px)",
      left: "calc(env(safe-area-inset-left, 0px) + 10px)",
      zIndex: 9,
      width: 96,
      padding: 4,
      border: "1px solid rgba(255,255,255,0.35)",
      borderRadius: 10,
      background: "rgba(0,0,0,0.6)",
      color: "#fff",
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      gap: 4,
      cursor: "pointer",
      boxShadow: "0 2px 10px rgba(0,0,0,0.45)",
      touchAction: "manipulation",
      ...noSave,
    }}
  >
    {thumbSrc && (
      <img
        src={thumbSrc}
        alt=""
        aria-hidden="true"
        draggable={false}
        style={{ display: "block", width: "100%", height: 60, objectFit: "cover", borderRadius: 6, background: "#222", pointerEvents: "none", ...noSave }}
      />
    )}
    <span style={{ fontSize: 12, lineHeight: "15px", fontWeight: 600, textAlign: "center", padding: "0 2px 2px", overflowWrap: "anywhere" }}>{label}</span>
  </button>
);

/** The head → feet slider with play / pause just under it, at the right edge — once the layers decode. */
export const RevealControls = ({ ctl }: { ctl: RevealController }) => {
  if (!ctl.controls || !ctl.active) return null;
  const { active } = ctl;
  return (
    <>
      <BandSlider c={active.c} bandHeight={ctl.bandHeight} onChange={(c) => ctl.scrub(active.i, c)} />
      <PlayPauseButton
        playing={ctl.playing}
        onToggle={ctl.playing ? ctl.pause : ctl.play}
        style={{ position: "absolute", right: "calc(env(safe-area-inset-right, 0px) + 12px)", top: "calc(76% + 10px)", zIndex: 8 }}
      />
    </>
  );
};

export const HintPill = ({ children, bottom = 56 }: { children: ReactNode; bottom?: number | string }) => (
  <div
    data-phx-hint-pill=""
    style={{
      position: "absolute",
      left: "50%",
      bottom,
      transform: "translateX(-50%)",
      zIndex: 8,
      pointerEvents: "none",
      background: "rgba(0, 0, 0, 0.6)",
      color: "#fff",
      fontSize: 13,
      lineHeight: "18px",
      padding: "6px 12px",
      borderRadius: 16,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </div>
);

export const RevealStyles = () => (
  <style>{`
    .phx-spot-target { padding: 0; border: none; background: transparent; cursor: zoom-in; -webkit-touch-callout: none; user-select: none; }
    .phx-spot-target:focus { outline: none; }
    .phx-spot-target:focus-visible { outline: 2px solid #fff; outline-offset: 2px; border-radius: 8px; }
    .phx-spot-spinner { width: 28px; height: 28px; border-radius: 50%; border: 3px solid rgba(255,255,255,0.35); border-top-color: #fff; animation: phx-spot-spin 0.8s linear infinite; }
    @keyframes phx-spot-spin { to { transform: rotate(360deg); } }
    .phx-person-hint { transform-origin: center; }
    .phx-person-hint-pulse { animation: phx-hint-pulse 1.4s ease-in-out 0.4s 3 both; }
    .phx-person-hint-pulse::after { content: ''; position: absolute; inset: 0; border-radius: 50%; box-shadow: 0 0 0 2px rgba(255,255,255,0.75); opacity: 0; animation: phx-hint-ring 1.4s ease-out 0.4s 3 both; }
    @keyframes phx-hint-pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.08); } }
    @keyframes phx-hint-ring { 0% { transform: scale(1); opacity: 0.7; } 100% { transform: scale(1.7); opacity: 0; } }
    .phx-whole-photo { transition: background 0.15s ease, border-color 0.15s ease; }
    .phx-whole-photo:hover { background: rgba(0,0,0,0.78); border-color: rgba(255,255,255,0.7); }
    .phx-whole-photo:focus { outline: none; }
    .phx-whole-photo:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
    @media (prefers-reduced-motion: reduce) { .phx-spot-spinner, .phx-person-hint-pulse, .phx-person-hint-pulse::after { animation: none; } }
  `}</style>
);

/**
 * Vertical head → feet slider for scanning a person (full-screen viewer). Continuous: the thumb follows
 * the pointer and sets the band centre directly. Right edge, clear of the subject.
 */
export const BandSlider = ({ c, bandHeight, onChange }: { c: number; bandHeight: number; onChange: (c: number) => void }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const lo = bandHeight / 2;
  const span = Math.max(1e-6, 1 - bandHeight);
  const at = Math.max(0, Math.min(1, (c - lo) / span));
  const pick = (clientY: number) => {
    const el = trackRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const p = Math.max(0, Math.min(1, (clientY - r.top) / Math.max(1, r.height)));
    onChange(lo + p * span);
  };
  const step = (d: number) => onChange(Math.max(lo, Math.min(1 - lo, c + d)));
  return (
    <div
      role="slider"
      aria-label="Scan person, head to feet"
      aria-orientation="vertical"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(at * 100)}
      aria-valuetext={`${Math.round(at * 100)}%`}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "ArrowDown" || e.key === "ArrowRight") { e.preventDefault(); step(0.05); }
        if (e.key === "ArrowUp" || e.key === "ArrowLeft") { e.preventDefault(); step(-0.05); }
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
        try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* pointer already gone */ }
        pick(e.clientY);
      }}
      onPointerMove={(e) => { if (e.buttons || e.pointerType !== "mouse") { e.stopPropagation(); pick(e.clientY); } }}
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        right: "calc(env(safe-area-inset-right, 0px) + 6px)",
        top: "20%",
        height: "56%",
        width: 56,
        zIndex: 8,
        touchAction: "none",
        display: "flex",
        justifyContent: "center",
        ...noSave,
      }}
    >
      <div ref={trackRef} style={{ position: "relative", width: 6, height: "100%", borderRadius: 3, background: "rgba(255,255,255,0.35)", boxShadow: "0 0 3px rgba(0,0,0,0.5)" }}>
        <div style={{ position: "absolute", left: 0, top: 0, width: "100%", height: `${at * 100}%`, borderRadius: 3, background: "rgba(255,255,255,0.85)" }} />
        <div
          data-phx-thumb=""
          style={{
            position: "absolute",
            left: "50%",
            top: `${at * 100}%`,
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: "#fff",
            boxShadow: "0 1px 6px rgba(0,0,0,0.5)",
            transform: "translate(-50%, -50%)",
          }}
        />
      </div>
    </div>
  );
};

/**
 * A port of the app's SpotlightImage overlay, for an image whose box IS the photo (the admin lightbox's
 * shrink-wrapped image, the Settings example's aspect box): fills its parent, a target over each
 * revealable person and an icon on their body; a click / tap opens the full-screen viewer on them.
 * Nothing reveals here. The largest person's layers are warmed shortly after the photo opens, in the
 * shape the viewer will use.
 */
export const PhotoPersonTargets = ({
  protect,
  base,
  layerUrl,
  onOpen,
  hint,
  activeIndex = null,
  splitView = false,
}: {
  protect: RevealProtect | null | undefined;
  base?: string;
  layerUrl?: (i: number, layer: RevealLayer) => string | undefined;
  onOpen: (i: number) => void;
  hint?: (touch: boolean) => string;
  /** The person shown in the split pane beside this lightbox (ringed); a click on another swaps them in. */
  activeIndex?: number | null;
  /** Reveals open in the desktop split pane (portrait reveals): warm those. */
  splitView?: boolean;
}) => {
  const regions = revealRegions(protect);
  const boxRef = useRef<HTMLDivElement>(null);
  const box = useBoxSize(boxRef);
  const ctl = useReveal({
    base: base || null,
    regions,
    protect,
    onTapOpen: onOpen,
    shape: (isPhone() || splitView) && tallOk(regions) ? "tall" : null,
    layerUrl,
  });
  const rect = box && box.width && box.height ? { left: 0, top: 0, width: box.width, height: box.height } : null;
  const place = (r: RevealRegion) => ({
    left: (r.x / 100) * (rect?.width ?? 0),
    top: (r.y / 100) * (rect?.height ?? 0),
    width: (r.width / 100) * (rect?.width ?? 0),
    height: (r.height / 100) * (rect?.height ?? 0),
  });
  const { touch } = ctl;
  return (
    <div ref={boxRef} style={{ position: "absolute", inset: 0, ...noSave }} onContextMenu={(e) => e.preventDefault()}>
      <PersonHints key={base} regions={regions} rect={rect} touch={touch} hidden={false} activeIndex={activeIndex} />
      {rect &&
        regions.map((r, i) => {
          const { touchAction, ref, ...tp } = ctl.targetProps(i);
          return <button type="button" key={i} ref={ref} {...tp} style={{ position: "absolute", zIndex: 5, touchAction, pointerEvents: "auto", ...place(r) }} />;
        })}
      {rect && regions.length > 0 && <HintPill>{hint ? hint(touch) : touch ? "Tap a person to see them clearly" : "Click a person to see them clearly"}</HintPill>}
      <RevealStyles />
    </div>
  );
};
